// src/constants/index.ts
import translations, { Language } from '../translations';

export const API_BASE_URL = (() => {
  const envUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
  // If running locally, dynamically adapt the hostname so mobile testing works without changing .env
  if (typeof window !== 'undefined' && (envUrl.includes('localhost') || envUrl.includes('192.168.') || envUrl.includes('172.'))) {
    const currentHostname = window.location.hostname;
    return envUrl.replace(/(https?:\/\/)([^:]+)(:\d+)?(.*)/, `$1${currentHostname}$3$4`);
  }
  return envUrl;
})();

// Dedicated env variable for uploads base URL (Fix #14 — avoids fragile string replace)
export const UPLOADS_BASE_URL = process.env.REACT_APP_UPLOADS_URL || API_BASE_URL.replace('/api/v1', '');

export const getImageUrl = (filename: string | undefined | null): string => {
  if (!filename) return '';
  // If it's already a full URL, return as-is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  // Strip any leading /uploads/ prefix to avoid double-pathing
  const cleanFilename = filename.replace(/^\/?uploads\//, '');
  return `${UPLOADS_BASE_URL}/uploads/${cleanFilename}`;
};

export const TOKEN_KEY = 'authToken';
export const USER_KEY = 'user';

export const ROUTES = {
  // Public
  HOME: '/',
  MENU: '/menu/:slug',

  // Auth
  LOGIN: '/login',

  // Admin
  ADMIN_DASHBOARD: '/admin',
  ADMIN_RESTAURANTS: '/admin/restaurants',
  ADMIN_RESTAURANTS_CREATE: '/admin/restaurants/create',
  ADMIN_RESTAURANTS_EDIT: '/admin/restaurants/:id/edit',
  ADMIN_RESTAURANT_STATS: '/admin/restaurants/:id/stats',
  ADMIN_RESTAURANTS_ALL_STATS: '/admin/restaurants/stats',

  // Restaurant Owner
  RESTAURANT_DASHBOARD: '/restaurant',
  RESTAURANT_SETTINGS: '/restaurant/settings',
  RESTAURANT_CATEGORIES: '/restaurant/categories',
  RESTAURANT_DISHES: '/restaurant/dishes',
  RESTAURANT_DISHES_CREATE: '/restaurant/dishes/create',
  RESTAURANT_DISHES_EDIT: '/restaurant/dishes/:id/edit',
  RESTAURANT_SCHEDULING: '/restaurant/scheduling',
  RESTAURANT_INVENTORY: '/restaurant/inventory',
  RESTAURANT_TEMPLATES: '/restaurant/templates',
  RESTAURANT_NOTIFICATIONS: '/restaurant/notifications',

  // ... existing routes

  // Cart & Orders (Public)
  CART: '/cart',
  CHECKOUT: '/checkout',
  ORDER_TRACK: '/order/track',
  ORDER_SUCCESS: '/order/success',

  // Restaurant Orders
  RESTAURANT_ORDERS: '/restaurant/orders',
  RESTAURANT_ORDER_DETAILS: '/restaurant/orders/:id',
  RESTAURANT_CASHIER: '/restaurant/cashier',

  // Restaurant Tables
  RESTAURANT_TABLES: '/restaurant/tables',
  RESTAURANT_TABLES_CREATE: '/restaurant/tables/create',

  // Restaurant Staff
  RESTAURANT_STAFF: '/restaurant/staff',
  RESTAURANT_PROFILE: '/restaurant/profile',

  // Accountant / Financial
  RESTAURANT_ACCOUNTANT: '/restaurant/accountant',
  RESTAURANT_EXPENSES: '/restaurant/expenses',
  RESTAURANT_REPORTS: '/restaurant/reports',
  RESTAURANT_CASH_REGISTER: '/restaurant/cash-register',
  RESTAURANT_DELIVERY_COMPANIES: '/restaurant/delivery-companies',
  RESTAURANT_DELIVERY_ORDERS: '/restaurant/delivery-orders',
  RESTAURANT_DELIVERY_CASHIER: '/restaurant/delivery-cashier',
  RESTAURANT_LEARNING_CENTER: '/restaurant/learning-center',
  RESTAURANT_PRINTER_SETTINGS: '/restaurant/printer-settings',
  RESTAURANT_PRINT_SERVER_MANAGER: '/restaurant/print-server-manager',
};

export const QUERY_KEYS = {
  // Auth
  ME: 'me',

  // Restaurants
  RESTAURANTS: 'restaurants',
  RESTAURANT: 'restaurant',
  RESTAURANT_PROFILE: 'restaurant-profile',

  // Categories
  CATEGORIES: 'categories',
  CATEGORY: 'category',

  // Dishes
  DISHES: 'dishes',
  DISH: 'dish',

  // Public
  PUBLIC_MENU: 'public-menu',

  // Inventory
  MATERIALS: 'materials',
  MATERIAL: 'material',
  TEMPLATES: 'templates',
  TEMPLATE: 'template',
  INVENTORY_SUMMARY: 'inventory-summary',
  STOCK_MOVEMENTS: 'stock-movements',

  // Staff
  STAFF: 'staff',

  // Financial
  EXPENSES: 'expenses',
  EXPENSE_SUMMARY: 'expense-summary',
  SHIFT_REPORTS: 'shift-reports',
  SHIFT_STAFF: 'shift-staff',
  PROFIT_LOSS: 'profit-loss',
  CASH_REGISTER: 'cash-register',
  CASH_REGISTER_ACTIVE: 'cash-register-active',
  CASH_REGISTER_CASHIERS: 'cash-register-cashiers',

  // Delivery Companies
  DELIVERY_COMPANIES: 'delivery-companies',
  DELIVERY_COMPANY_ORDERS: 'delivery-company-orders',
  DELIVERY_COMPANY_REPORT: 'delivery-company-report',
};

const getTranslation = (key: string): string => {
  const lang = (localStorage.getItem('appLanguage') || 'ar') as Language;
  return translations[lang]?.[key] || translations['ar']?.[key] || key;
};

export const MESSAGES = {
  SUCCESS: {
    get LOGIN() { return getTranslation('messages.success.login'); },
    get LOGOUT() { return getTranslation('messages.success.logout'); },
    get CREATED() { return getTranslation('messages.success.created'); },
    get UPDATED() { return getTranslation('messages.success.updated'); },
    get DELETED() { return getTranslation('messages.success.deleted'); },
  },
  ERROR: {
    get GENERIC() { return getTranslation('messages.error.generic'); },
    get UNAUTHORIZED() { return getTranslation('messages.error.unauthorized'); },
    get NETWORK() { return getTranslation('messages.error.network'); },
    get VALIDATION() { return getTranslation('messages.error.validation'); },
  },
};

export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
};

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50],
};

export const getOrderStatusLabel = (status: string): string => getTranslation(`orderStatus.${status}`);
export const getTableStatusLabel = (status: string): string => getTranslation(`tableStatus.${status}`);
export const getOrderTypeLabel = (type: string): string => getTranslation(`orderType.${type}`);

// Keep backward-compatible objects using getters
export const ORDER_STATUS_LABELS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getTranslation(`orderStatus.${key}`),
});

export const TABLE_STATUS_LABELS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getTranslation(`tableStatus.${key}`),
});

// Order Status Colors
export const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: '#eab308', // Yellow
  confirmed: '#3b82f6',
  preparing: '#6523d1',
  completed: '#22c55e',
  returned: '#9333ea', // Purple
  cancelled: '#ef4444',
};

// Order Type Labels
export const ORDER_TYPE_LABELS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getTranslation(`orderType.${key}`),
});
